import { c as _c } from "react/compiler-runtime";
import { Badge, Heading, HStack, Separator, Stack, Text, useRecipe } from '@chakra-ui/react';
import type { ScoProduct } from '@src/graphql-types';
import { paywallProductCardRecipe } from '@src/theme/recipes/paywall-product-card';
import { PaywallFeatureList } from './PaywallFeatureList';
import { PlanChoiceButton } from './PlanChoiceButton';
import { cardFeatureGroups } from './cardLists';
import { resolvePaywallIntent, type PaywallIntentId } from './intent';
import { billingTermChangeLabel, termSavingsPercent } from './offers';
import { FREE_PLAN_ID } from './planFeatures';
import { productCanContinue, productCtaLabel, productDescription, productLabel, productOfferState, productPriceDisplay, productPriceLabel, productPromotion } from './presentation';
export type PaywallCardAppearance = 'standard' | 'spotlight' | 'quiet';
export type PaywallProductCardProps = {
  product: ScoProduct;
  appearance?: PaywallCardAppearance;
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  ctaLabel?: string;
  currentProductIds?: string[];
  /** Billing term already paid, keyed by productId. See `productOfferState`. */
  currentTermByProductId?: Record<string, string | null>;
  eligibleProductIds?: string[];
  /** A cheaper listed plan this one contains; see `includedPlanFor`. */
  includedPlan?: ScoProduct | null;
  /** Caller-verified reassurance under the CTA, such as “Cancel anytime.” Never inferred here. */
  assurance?: string;
  onContinue?: (product: ScoProduct) => void;
};

/** A card lists this many lines before "+ N more"; the compare below carries the full set. */
const CARD_FEATURE_LIMIT = 6;
export const PaywallProductCard = t0 => {
  const $ = _c(92);
  const {
    appearance: t1,
    assurance,
    ctaLabel,
    currentProductIds,
    currentTermByProductId,
    eligibleProductIds,
    includedPlan,
    intent,
    onContinue,
    product,
    tokens
  } = t0;
  const appearance = t1 === undefined ? "standard" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      recipe: paywallProductCardRecipe
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const cardRecipe = useRecipe(t2);
  let T0;
  let action;
  let current;
  let header;
  let included;
  let priceBlock;
  let recommended;
  let spotlight;
  let t3;
  let t4;
  let t5;
  if ($[1] !== appearance || $[2] !== assurance || $[3] !== cardRecipe || $[4] !== ctaLabel || $[5] !== currentProductIds || $[6] !== currentTermByProductId || $[7] !== eligibleProductIds || $[8] !== includedPlan || $[9] !== intent || $[10] !== onContinue || $[11] !== product || $[12] !== tokens) {
    const resolved = resolvePaywallIntent(intent, tokens);
    const {
      current: t6,
      recommended: t7,
      showAction,
      termChange
    } = productOfferState(product, resolved, {
      currentProductIds,
      currentTermByProductId,
      eligibleProductIds
    });
    current = t6;
    recommended = t7;
    let t8;
    if ($[24] !== product) {
      t8 = productLabel(product);
      $[24] = product;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    const name = t8;
    const viewedTerm = product.effectiveTermId || product.termId || "";
    const upgrade = !current && !termChange && Boolean(currentProductIds?.length);
    const continueLabel = ctaLabel?.trim() || (termChange && viewedTerm ? billingTermChangeLabel(termChange, viewedTerm) : "") || productCtaLabel(product, resolved, undefined, {
      upgrade
    });
    let t9;
    if ($[26] !== product) {
      t9 = productPriceLabel(product);
      $[26] = product;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    const price = t9;
    let t10;
    if ($[28] !== product) {
      t10 = productPriceDisplay(product);
      $[28] = product;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    const display = t10;
    let t11;
    if ($[30] !== product) {
      t11 = productDescription(product);
      $[30] = product;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    const description = t11;
    let t12;
    if ($[32] !== product) {
      t12 = productPromotion(product);
      $[32] = product;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    const promotion = t12;
    const termId = product.effectiveTermId || product.termId;
    let t13;
    if ($[34] !== product || $[35] !== termId) {
      t13 = termId && termId !== "month" ? termSavingsPercent(product, termId) : null;
      $[34] = product;
      $[35] = termId;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    const savings = t13;
    const assuranceLine = assurance?.trim() && !current && product.productId !== FREE_PLAN_ID ? assurance.trim() : null;
    spotlight = appearance === "spotlight";
    const composedCard = cardFeatureGroups(product, intent ?? "default") != null;
    const t14 = spotlight ? "xl" : "lg";
    let t15;
    if ($[37] !== name || $[38] !== t14) {
      t15 = <Heading as="h2" size={t14}>{name}</Heading>;
      $[37] = name;
      $[38] = t14;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    let t16;
    if ($[40] !== description) {
      t16 = description ? <Text color="fg.muted" textStyle="sm" textWrap="pretty">{description}</Text> : null;
      $[40] = description;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== t15 || $[43] !== t16) {
      t17 = <Stack gap={1.5}>{t15}{t16}</Stack>;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    header = t17;
    const t18 = spotlight ? "5xl" : "4xl";
    let t19;
    if ($[45] !== display.amount || $[46] !== t18) {
      t19 = <Text fontVariantNumeric="tabular-nums" fontWeight="bold" letterSpacing="-0.03em" lineHeight="1" textStyle={t18}>{display.amount}</Text>;
      $[45] = display.amount;
      $[46] = t18;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] !== display.period) {
      t20 = display.period ? <Text color="fg.muted" textStyle="sm">{display.period}</Text> : null;
      $[48] = display.period;
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    let t21;
    if ($[50] !== promotion) {
      t21 = promotion?.listPrice ? <Text color="fg.muted" textDecoration="line-through" textStyle="sm">{promotion.listPrice}</Text> : null;
      $[50] = promotion;
      $[51] = t21;
    } else {
      t21 = $[51];
    }
    let t22;
    if ($[52] !== t19 || $[53] !== t20 || $[54] !== t21) {
      t22 = <HStack align="baseline" gap={1.5} wrap="wrap">{t19}{t20}{t21}</HStack>;
      $[52] = t19;
      $[53] = t20;
      $[54] = t21;
      $[55] = t22;
    } else {
      t22 = $[55];
    }
    let t23;
    if ($[56] !== display.note || $[57] !== promotion || $[58] !== savings) {
      t23 = display.note || promotion || savings != null && savings > 0 ? <HStack gap={2} wrap="wrap">{promotion ? <Badge colorPalette="green" variant="surface">{promotion.label}</Badge> : savings != null && savings > 0 ? <Badge colorPalette="green" variant="surface">{`Save ${savings}%`}</Badge> : null}{display.note ? <Text color="fg.muted" textStyle="xs">{display.note}</Text> : null}</HStack> : null;
      $[56] = display.note;
      $[57] = promotion;
      $[58] = savings;
      $[59] = t23;
    } else {
      t23 = $[59];
    }
    let t24;
    if ($[60] !== t22 || $[61] !== t23) {
      t24 = <Stack gap={1.5}>{t22}{t23}</Stack>;
      $[60] = t22;
      $[61] = t23;
      $[62] = t24;
    } else {
      t24 = $[62];
    }
    priceBlock = t24;
    action = showAction ? <Stack gap={2}><PlanChoiceButton aria-label={current ? `${name}, your current plan` : `${continueLabel}, ${price}`} disabled={current || !productCanContinue(product)} h="auto" product={product} py={spotlight ? 3.5 : 3} textAlign="center" variant={recommended && !current ? "cta" : "outline"} whiteSpace="normal" width="full" onChoose={!current && onContinue ? () => onContinue(product) : undefined}>{current ? "Your current plan" : spotlight ? <Stack align="center" gap={0}><Text>{continueLabel}</Text><Text fontWeight="normal" textStyle="xs">{price}</Text></Stack> : continueLabel}</PlanChoiceButton>{assuranceLine ? <Text color="fg.muted" textAlign="center" textStyle="xs">{assuranceLine}</Text> : null}</Stack> : <span aria-hidden={true} />;
    let t25;
    if ($[63] !== appearance) {
      t25 = appearance === "quiet" ? null : <Separator />;
      $[63] = appearance;
      $[64] = t25;
    } else {
      t25 = $[64];
    }
    const t26 = composedCard ? undefined : includedPlan;
    const t27 = composedCard ? undefined : CARD_FEATURE_LIMIT;
    let t28;
    if ($[65] !== intent || $[66] !== product || $[67] !== t26 || $[68] !== t27) {
      t28 = <PaywallFeatureList includedPlan={t26} intent={intent} limit={t27} product={product} />;
      $[65] = intent;
      $[66] = product;
      $[67] = t26;
      $[68] = t27;
      $[69] = t28;
    } else {
      t28 = $[69];
    }
    let t29;
    if ($[70] !== t25 || $[71] !== t28) {
      t29 = <Stack gap={4}>{t25}{t28}</Stack>;
      $[70] = t25;
      $[71] = t28;
      $[72] = t29;
    } else {
      t29 = $[72];
    }
    included = t29;
    T0 = Stack;
    t3 = name;
    t4 = "article";
    t5 = cardRecipe({
      appearance,
      recommended
    });
    $[1] = appearance;
    $[2] = assurance;
    $[3] = cardRecipe;
    $[4] = ctaLabel;
    $[5] = currentProductIds;
    $[6] = currentTermByProductId;
    $[7] = eligibleProductIds;
    $[8] = includedPlan;
    $[9] = intent;
    $[10] = onContinue;
    $[11] = product;
    $[12] = tokens;
    $[13] = T0;
    $[14] = action;
    $[15] = current;
    $[16] = header;
    $[17] = included;
    $[18] = priceBlock;
    $[19] = recommended;
    $[20] = spotlight;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    T0 = $[13];
    action = $[14];
    current = $[15];
    header = $[16];
    included = $[17];
    priceBlock = $[18];
    recommended = $[19];
    spotlight = $[20];
    t3 = $[21];
    t4 = $[22];
    t5 = $[23];
  }
  const t6 = recommended ? "true" : "false";
  let t7;
  if ($[73] !== current || $[74] !== recommended || $[75] !== spotlight) {
    t7 = current || recommended ? <Badge colorPalette={current ? "gray" : "orange"} insetStart={{
      base: 5,
      md: spotlight ? 8 : 6
    }} position="absolute" top={0} transform="translateY(-50%)" variant={current ? "surface" : "solid"}>{current ? "Current plan" : "Recommended"}</Badge> : null;
    $[73] = current;
    $[74] = recommended;
    $[75] = spotlight;
    $[76] = t7;
  } else {
    t7 = $[76];
  }
  let t8;
  if ($[77] !== action || $[78] !== appearance || $[79] !== included) {
    t8 = appearance === "quiet" ? <>{included}{action}</> : <>{action}{included}</>;
    $[77] = action;
    $[78] = appearance;
    $[79] = included;
    $[80] = t8;
  } else {
    t8 = $[80];
  }
  let t9;
  if ($[81] !== T0 || $[82] !== appearance || $[83] !== header || $[84] !== priceBlock || $[85] !== t3 || $[86] !== t4 || $[87] !== t5 || $[88] !== t6 || $[89] !== t7 || $[90] !== t8) {
    t9 = <T0 aria-label={t3} as={t4} css={t5} data-appearance={appearance} data-recommended={t6}>{t7}{header}{priceBlock}{t8}</T0>;
    $[81] = T0;
    $[82] = appearance;
    $[83] = header;
    $[84] = priceBlock;
    $[85] = t3;
    $[86] = t4;
    $[87] = t5;
    $[88] = t6;
    $[89] = t7;
    $[90] = t8;
    $[91] = t9;
  } else {
    t9 = $[91];
  }
  return t9;
};